import { PriceItem } from '@/features/common/components/Overview/types'
import {
    AccountOrder,
    AccountOrderItem,
    AccountPaymentItem,
    AccountPriceBreakdown,
    OpenTabAccountSummary,
} from '@/features/openTabs/types'
import {
    getAccountAmountDue,
    getAccountDisplayAmount,
    getAccountItemTotal,
} from '@/features/openTabs/utils/accountCalculations'
import { formatFreeOfChargeAblePrice } from '@/utils/formatFreeOfChargeAblePrice'
import { formatPrice } from '@/utils/priceHelper'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

const formatQuantity = (quantity: number): string =>
    (Number.isInteger(quantity)
        ? String(quantity)
        : String(Math.round(quantity * 100) / 100)
    ).replace('.', ',')

export const formatAccountPrice = (amount: number | string, currency: string): string =>
    formatPrice(String(amount), currency, false, 2)

export const formatAccountListPrice = (amount: number | string, currency: string): string =>
    formatPrice(String(amount), currency)

export const toAccountPriceItems = (
    breakdown: AccountPriceBreakdown,
    currency: string,
    t: TFunction<'app'>
): PriceItem[] => [
    {
        label: t('common.overview.prices.items'),
        price: formatAccountListPrice(breakdown.productsAmount, currency),
    },
    {
        label: t('common.overview.prices.container'),
        price: formatAccountListPrice(breakdown.containerAmount, currency),
    },
]

export const toAccountListItem = (account: OpenTabAccountSummary) => ({
    id: account.id,
    number: account.number,
    name: account.name,
    total: formatAccountListPrice(getAccountDisplayAmount(account), account.currency),
    paymentCount: account.status === 'closed' ? account.paymentCount : undefined,
})

const formatModifierPrice = (amount: number, currency: string, t: TFunction<'app'>) =>
    formatFreeOfChargeAblePrice({ amount: String(amount), currency }, t, false, 2)

const formatModifier = (item: AccountOrderItem, modifierIndex: number, t: TFunction<'app'>) => {
    const modifier = item.modifiers[modifierIndex]
    const quantity = `${formatQuantity(modifier.quantity)}x `
    const price = ` (${formatModifierPrice(modifier.unitPrice, item.currency, t)})`

    return {
        id: modifier.id,
        label: `${quantity}${modifier.name}${price}`,
    }
}

export const toAccountItemView = (item: AccountOrderItem, t: TFunction<'app'>) => ({
    id: item.id,
    name: `${item.name} (${formatAccountPrice(item.baseUnitPrice, item.currency)})`,
    quantity: item.quantity,
    canRemove: item.settledQuantity === 0,
    totalPrice: formatAccountPrice(item.totalAmount, item.currency),
    additions: item.modifiers
        .map((modifier, index) => ({ modifier, index }))
        .filter(({ modifier }) => modifier.type === 'addition')
        .map(({ index }) => formatModifier(item, index, t)),
    removals: item.modifiers
        .filter((modifier) => modifier.type === 'removed-ingredient')
        .map((modifier) => modifier.name),
    note: item.note,
})

export const toOrderView = (order: AccountOrder, t: TFunction<'app'>) => ({
    id: order.id,
    title: t('openTabs.order.title', { number: order.number }),
    time: dayjs(order.createdAt).format('HH:mm'),
    totalPrice: formatAccountListPrice(order.totalAmount, order.currency),
    items: order.items.map((item) => toAccountItemView(item, t)),
})

export const toSplitItemView = (
    { item, quantity }: AccountPaymentItem,
    t: TFunction<'app'>,
    paymentTotal?: number
) => ({
    id: item.id,
    name: `${item.name} (${formatAccountPrice(item.baseUnitPrice, item.currency)})`,
    quantity,
    totalPrice: formatAccountPrice(
        paymentTotal ?? getAccountItemTotal(item, quantity),
        item.currency
    ),
    modifiers: item.modifiers
        .filter((modifier) => modifier.type === 'addition')
        .map((modifier) => {
            const quantityShare = modifier.quantity * quantity

            return `${formatQuantity(quantityShare)}x ${modifier.name} (${formatModifierPrice(modifier.unitPrice, item.currency, t)})`
        }),
})

export const getAccountTotalPrice = (account: OpenTabAccountSummary): string =>
    formatAccountPrice(
        account.status === 'closed'
            ? getAccountDisplayAmount(account)
            : getAccountAmountDue(account),
        account.currency
    )
